import React from 'react';
import WebflowSlider from '@packages/systems/design-system/WebflowSlider';
import {Cell} from '@packages/systems/design-system/Layout';
import {styled, useTheme} from '@packages/utilities/styled';
import {HStack} from '@packages/systems/spring';
import {selectLocalizationKeyForExpression} from '@packages/domains/localization/dom/flux';
import {
  Expressions,
  Plugins,
  getMeta,
  TypeImplementation,
  Component,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import {TText} from '@packages/systems/wfdl-ast';
import {
  selectCurrentComponent,
  selectSelectedElement,
} from '@packages/utilities/platform-selectors';
import {NAVBAR_COLLAPSE_CHANGED} from '@packages/systems/designer/actions';

import {stripAll, getAnn} from '@packages/utilities/contextual-expression';
import {findFurthestAncestor} from '@packages/utilities/platform';
import {isNavbarWrapper} from '@packages/utilities/elementTypeUtils';
import {
  useDesignerSelector,
  useDispatch,
  useFluxContext,
  type DesignerFlux,
  type DesignerStores,
} from '@packages/systems/designer/flux';

const NAVBAR_COLLAPSE_PATH: ExpressionPath = [
  {in: 'Record', at: 'navbar'},
  {in: 'Record', at: 'collapse'},
];

const SliderBar = styled(WebflowSlider)(({theme: {gutters}}) => ({
  flexGrow: 1,
  marginLeft: gutters.normal,
  marginRight: gutters.normal,
}));

const value = {min: 0, max: 4, step: 1} as const;

const COLLAPSE_DEFAULT_VALUE = 1;

const COLLAPSE_LABELS = {
  all: 'Always appear',
  medium: 'Tablet and below',
  small: 'Phone (L) and below',
  tiny: 'Phone (P) and below',
  none: 'None',
} as const;

const COLLAPSE_NUMBER_MAP: {
  [key: string]: number;
} = {
  all: 0,
  medium: 1,
  small: 2,
  tiny: 3,
  none: 4,
};

const Label = styled('span')(({theme: {gutters, colors}}) => ({
  marginLeft: gutters.base,
  fontStyle: 'italic',
  color: colors.foregroundDimmer,
}));

const CollapseIcon = ({
  alignCenter = false,
  // @ts-expect-error - TS7031 - Binding element 'collapseNumberConditional' implicitly has an 'any' type.
  collapseNumberConditional,
  // @ts-expect-error - TS7031 - Binding element 'icon' implicitly has an 'any' type.
  icon,
  // @ts-expect-error - TS7031 - Binding element 'onChange' implicitly has an 'any' type.
  onChange,
  // @ts-expect-error - TS7031 - Binding element 'scale' implicitly has an 'any' type.
  scale,
  // @ts-expect-error - TS7031 - Binding element 'title' implicitly has an 'any' type.
  title,
  // @ts-expect-error - TS7031 - Binding element 'translate' implicitly has an 'any' type.
  translate,
}) => {
  const {colors} = useTheme();

  return (
    <Cell
      align={alignCenter ? 'center' : 'start'}
      onClick={onChange}
      style={{transform: `translate(${translate})`}}
      title={title}
    >
      {collapseNumberConditional ? (
        <Plugins.PluginIcon icon={icon} scale={scale} />
      ) : (
        <Plugins.PluginIcon
          icon={icon}
          scale={scale}
          style={{color: colors.foregroundDimmer}}
        />
      )}
    </Cell>
  );
};

const selectStyleBlockStore = (state: DesignerStores) => state.StyleBlockStore;

const SliderNavbarCollapseType = TypeImplementation.Alias({
  type: TText,
  render: function SliderNavbarCollapseRender({expr}) {
    const styleBlockState = useDesignerSelector(selectStyleBlockStore);
    const strippedExpr = stripAll(expr);

    const {type} = getAnn(expr);

    const currentComponent = useDesignerSelector(selectCurrentComponent);
    const rootElement = Component.getRender(currentComponent);
    const selectedElement = useDesignerSelector(selectSelectedElement);
    const wfDispatch = useDispatch();
    const flux = useFluxContext<DesignerFlux>();
    const mediaQueryList = styleBlockState.getOrderedBreakpointIds();

    if (type === null) {
      throw Error('Expected type from AnnotatedExpression to be defined');
    }

    const maybeCollapseTextValue = Expressions.getText(strippedExpr);

    const collapseTextValue = maybeCollapseTextValue
      ? maybeCollapseTextValue
      : 'medium';

    const collapseNumberValue = maybeCollapseTextValue
      ? COLLAPSE_NUMBER_MAP[maybeCollapseTextValue]
      : COLLAPSE_DEFAULT_VALUE;

    const onChange = (collapseNumber = COLLAPSE_DEFAULT_VALUE) => {
      if (selectedElement) {
        const navbarWrapper = isNavbarWrapper(selectedElement)
          ? selectedElement
          : findFurthestAncestor(rootElement, selectedElement, isNavbarWrapper);

        if (navbarWrapper) {
          const localizationKey = selectLocalizationKeyForExpression(
            flux.state,
            navbarWrapper.id,
            NAVBAR_COLLAPSE_PATH
          );
          wfDispatch({
            type: NAVBAR_COLLAPSE_CHANGED,
            payload: {
              navbarWrapperId: navbarWrapper.id,
              collapse: Object.keys(COLLAPSE_NUMBER_MAP)[collapseNumber],
              mediaQueryList,
              localizationKey,
            },
          });
        }
      }
    };

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly all: "Always appear"; readonly medium: "Tablet and below"; readonly small: "Phone (L) and below"; readonly tiny: "Phone (P) and below"; readonly none: "None"; }'.
    const collapseLabel = COLLAPSE_LABELS[collapseTextValue];
    const meta = getMeta(type);
    const metaIcons = meta ? meta.icons : {};

    return (
      <div data-automation-id="SliderNavbarCollapseType">
        <HStack style={{margin: '10px 0'}}>
          Menu icon for:<Label>{collapseLabel}</Label>
        </HStack>
        <HStack
          justify="space-between"
          align="center"
          style={{margin: '0 2px'}}
        >
          {/* All Devices */}
          <CollapseIcon
            alignCenter
            collapseNumberConditional={collapseNumberValue === 0}
            icon={metaIcons.all}
            onChange={() => onChange(0)}
            scale={1.4}
            title="All Devices"
            translate="3px"
          />
          {/* Tablet */}
          <CollapseIcon
            // @ts-expect-error - TS18048 - 'collapseNumberValue' is possibly 'undefined'.
            collapseNumberConditional={collapseNumberValue <= 1}
            icon={metaIcons.medium}
            onChange={() => onChange(1)}
            scale={1.4}
            title="Tablet"
            translate="3px"
          />
          {/* Phone Landscape */}
          <CollapseIcon
            // @ts-expect-error - TS18048 - 'collapseNumberValue' is possibly 'undefined'.
            collapseNumberConditional={collapseNumberValue <= 2}
            icon={metaIcons.small}
            onChange={() => onChange(2)}
            scale={1.4}
            title="Phone Landscape"
            translate="1px"
          />
          {/* Phone Portrait */}
          <CollapseIcon
            // @ts-expect-error - TS18048 - 'collapseNumberValue' is possibly 'undefined'.
            collapseNumberConditional={collapseNumberValue <= 3}
            icon={metaIcons.tiny}
            onChange={() => onChange(3)}
            scale={1.4}
            title="Phone Portrait"
            translate="-2px"
          />
          {/* None */}
          <CollapseIcon
            collapseNumberConditional={collapseNumberValue === 4}
            icon={metaIcons.none}
            onChange={() => onChange(4)}
            scale={1.2}
            title="None"
            translate="-1px"
          />
        </HStack>
        <HStack style={{margin: '0 0 0 3px'}}>
          <SliderBar
            data-automation-id="slider-type-bar"
            disabled={false}
            // @ts-expect-error - error TS2322 - Type 'number | undefined' is not assignable to type 'number'.
            value={collapseNumberValue}
            onChange={onChange}
            min={value.min}
            max={value.max}
            step={value.step}
          />
        </HStack>
      </div>
    );
  },
});

export default SliderNavbarCollapseType;
